import { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { TriggerButton } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import { type INavPanelCategory } from "../core/navigation-panel/NavigationPanel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import PageLayout from "../core/navigation-panel/PageLayout.js";
import { showNavbarElement } from "@/redux/modules/navbar.ts";
import { type IProjectListState, loadProjectList } from "@/redux/modules/project-list.ts";
import { type ITableColumn } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { CELL_TYPE } from "@/settings/settings.js";

export const PROJECT_LISTING_ITEMS: INavPanelCategory[] = [
  {
    label: "Projects",
    tooltip: "Show all the projects",
    navItems: [
      {
        label: "Overview",
        route: "/",
        pageComponent: <ContentBrowserProjectsTablePage />,
      },
    ],
  },
];

export default function ProjectsListingPage() {
  return <PageLayout navPanelCategories={PROJECT_LISTING_ITEMS} navPanelCollapsed />;
}

const projectsListingTableColumns: Array<Partial<ITableColumn>> = [
  { name: "Name", field: "project.name", cellType: CELL_TYPE.STRING, fixed: true },
  { name: "Description", field: "project.description", cellType: CELL_TYPE.STRING },
  { name: "Creator", field: "project.creator.display", cellType: CELL_TYPE.STRING },
  { name: "Created", field: "project.created", cellType: CELL_TYPE.DATE_TIME },
  { name: "Last Use", field: "project.lastModified", cellType: CELL_TYPE.DATE_TIME },
  { name: "User Group", field: "project.userGroup.name", cellType: CELL_TYPE.STRING },
  { name: "Start Date", field: "project.startDate", cellType: CELL_TYPE.DATE },
  { name: "Last Update", field: "project.lastUpdate", cellType: CELL_TYPE.DATE_TIME },
  { name: "Initial End Date", field: "project.initialEndDate", cellType: CELL_TYPE.DATE },
  { name: "Actual End Date", field: "project.actualEndDate", cellType: CELL_TYPE.DATE },
  { name: "Projected End Date", field: "project.projectedEndDate", cellType: CELL_TYPE.DATE },
  { name: "Goal", field: "project.goal", cellType: CELL_TYPE.STRING },
  { name: "Status", field: "project.status", cellType: CELL_TYPE.STRING },
  // { name: "Status Notes", field: "project.statusNotes", cellType: settings.CELL_TYPE.STRING, hidden: true },
  // { name: "End Notes", field: "project.endNotes", cellType: settings.CELL_TYPE.STRING, hidden: true },
];

function ContentBrowserProjectsTablePage() {
  const dispatch = useDispatch<ReduxDispatch>();

  const projectList = useSelector<IAppState, IProjectListState>((state) => state.projectList, shallowEqual);
  const showCreateProjectDialog = useSelector<IAppState, boolean>(
    (state) => state.navBar.showCreateProjectDialog,
    shallowEqual,
  );

  useEffect(() => {
    dispatch(loadProjectList());
  }, [dispatch]);

  const toggleShowCreateProjectModal = () =>
    dispatch(showNavbarElement({ showCreateProjectDialog: !showCreateProjectDialog }));

  return (
    <>
      <PageHeader
        title={"Projects"}
        setDocumentTitle
        badge={String((projectList?.projects || []).length)}
        actionWidgets={[
          <TriggerButton
            key={"content-browser-projects-table-page-action-1"}
            icon={"create_new_folder"}
            isDisabled={showCreateProjectDialog}
            onPress={toggleShowCreateProjectModal}
            label={"New Project"}
            showLabel
          />,
        ]}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <InMemoryTableWithSelectionModel
          columns={projectsListingTableColumns}
          rowGridData={projectList?.projects.map((p) => ({ id: p.id, project: p }))}
          uniqueGridIdentifier={"projects-list"}
          useSelectionModel={false}
          isLoading={projectList?.isLoading}
        />
      </div>
    </>
  );
}
